import React from 'react';
import * as styles from '../../styles/components/lazy-visualization-placeholder.css';

/**
 * LazyVisualizationPlaceholderのプロパティ
 */
export interface LazyVisualizationPlaceholderProps {
  /**
   * データの行数
   */
  dataSize: number;

  /**
   * ユーザーが「View」ボタンをクリックした際のコールバック
   */
  onView: () => void;

  /**
   * 可視化の種類（例: "histogram", "scatter plot"）
   * @default "visualization"
   */
  visualizationType?: string;

  /**
   * プレースホルダーの高さ（可視化と同じ高さにする）
   * @default 200
   */
  height?: number;

  /**
   * ローディング中かどうか
   * @default false
   */
  isLoading?: boolean;

  /**
   * エラー情報
   * @default null
   */
  error?: Error | null;

  /**
   * リトライボタンクリック時のコールバック
   */
  onRetry?: () => void;
}

/**
 * 大規模データセットの可視化がまだレンダリングされていない場合に表示される
 * 標準化されたプレースホルダーUI
 *
 * 3つの状態を持つ：
 * - 初期状態：データサイズとViewボタン表示
 * - ローディング中：スピナーアニメーションと"Rendering..."テキスト
 * - エラー時：エラーメッセージとRetryボタン
 *
 * @example
 * ```tsx
 * <LazyVisualizationPlaceholder
 *   dataSize={1500}
 *   onView={requestRender}
 *   visualizationType="histogram"
 *   height={200}
 *   isLoading={isLoading}
 *   error={error}
 *   onRetry={retry}
 * />
 * ```
 */
export function LazyVisualizationPlaceholder({
  dataSize,
  onView,
  visualizationType = 'visualization',
  height = 200,
  isLoading = false,
  error = null,
  onRetry
}: LazyVisualizationPlaceholderProps): React.ReactElement {
  return (
    <div
      className={styles.lazyPlaceholder}
      style={{ height }}
      data-testid="lazy-visualization-placeholder"
    >
      <div className={styles.placeholderContent}>
        {/* ローディング中の表示 */}
        {isLoading && (
          <>
            <div className={styles.spinner} aria-label="Loading" />
            <div className={styles.loadingText}>
              Rendering {visualizationType}...
            </div>
          </>
        )}

        {/* エラー時の表示 */}
        {error && !isLoading && (
          <>
            <span className={styles.errorIcon}>⚠️</span>
            <div className={styles.errorMessage}>
              Failed to render {visualizationType}
            </div>
            {onRetry && (
              <button
                className={styles.retryButton}
                onClick={onRetry}
                aria-label={`Retry rendering ${visualizationType}`}
              >
                Retry
              </button>
            )}
          </>
        )}

        {/* 初期状態の表示 */}
        {!isLoading && !error && (
          <>
            <span className={styles.placeholderIcon}>📊</span>
            <div className={styles.placeholderInfo}>
              <span className={styles.dataSize}>
                {dataSize.toLocaleString()} rows
              </span>
            </div>
            <button
              className={styles.viewButton}
              onClick={onView}
              disabled={isLoading}
              aria-label={`View ${visualizationType} for ${dataSize.toLocaleString()} rows`}
            >
              View {visualizationType}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
